import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RiEditLine } from '@remixicon/react';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import DistanceField, {
  type DistanceDraft,
  distanceDraftOf,
  distanceError,
  typedDistance,
} from '@/pages/warehouse/components/DistanceField';
import OutboundDestinationStep, {
  type DestinationDraft,
  type DestinationField,
} from '@/pages/warehouse/components/OutboundDestinationStep';
import { FormField } from '@/pages/warehouse/components/form';
import type { Pitstop } from '@/services/api/pitstops';
import { Shipment, shipmentsAPI } from '@/services/api/shipments';

import { canSetHub, HUB_SKIP_REASON } from '../shipmentHandover';
import { HandoverBadge } from './ShipmentBadges';
import { useSetHubOptions } from './useSetHubOptions';

/** Statuses whose recipient + destination ops may still edit (pre-dispatch). */
const EDITABLE_STATUSES = ['CREATED', 'AT_HUB'];

/**
 * "Ubah" shown for a shipment still before dispatch. DIRECT_4W keeps its
 * destination on the route plan. The server has the final word (items with a
 * rider or a delivery-service delivery → 409 SHIPMENT_NOT_EDITABLE).
 */
export const canEditShipmentDestination = (
  s: Pick<Shipment, 'status' | 'type'>
): boolean => EDITABLE_STATUSES.includes(s.status) && s.type !== 'DIRECT_4W';

interface Props {
  /** The shipment to edit; null = closed. */
  shipment: Shipment | null;
  /** Dash hubs (staffed HUB pitstops) for the Hub field; the field shows
   *  only for a shipment "Set hub" applies to (`canSetHub`). */
  hubs?: Pitstop[];
  onClose: () => void;
  onSaved: (updated: Shipment) => void;
}

const NO_HUBS: Pitstop[] = [];

/** The address fields a Retrieval takes over from its hub/pitstop. */
const addressOf = (p: Pitstop): Partial<DestinationDraft> => ({
  address: p.address ?? '',
  latitude: String(p.latitude),
  longitude: String(p.longitude),
});

const sameDraft = (a: DestinationDraft, b: DestinationDraft): boolean =>
  a.recipientName === b.recipientName &&
  a.recipientPhone === b.recipientPhone &&
  a.address === b.address &&
  a.latitude === b.latitude &&
  a.longitude === b.longitude &&
  a.destNotes === b.destNotes;

const draftOf = (s: Shipment): DestinationDraft => ({
  recipientName: s.receiverName ?? '',
  recipientPhone: s.receiverPhone ?? '',
  address: s.destinationAddress ?? '',
  latitude: String(s.destinationLat ?? ''),
  longitude: String(s.destinationLong ?? ''),
  destNotes: s.destinationNotes ?? '',
  manual: true,
  matched: null,
  // Shipments carry no WMS city: the step hides the field (addressOnly).
  city: null,
  cityTouched: false,
  cityWarning: null,
});

const coord = (raw: string): number | null => {
  const t = raw.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Required-input guard; the server validates again. */
const localErrors = (
  d: DestinationDraft
): Partial<Record<DestinationField, string>> => {
  const out: Partial<Record<DestinationField, string>> = {};
  if (!d.recipientName.trim()) out.recipientName = 'Nama penerima wajib diisi.';
  if (!d.recipientPhone.trim()) out.recipientPhone = 'Telepon wajib diisi.';
  if (!d.address.trim()) out.address = 'Alamat tujuan wajib diisi.';
  const lat = coord(d.latitude);
  const lng = coord(d.longitude);
  if (lat === null || Math.abs(lat) > 90)
    out.latitude = 'Latitude harus angka -90…90.';
  if (lng === null || Math.abs(lng) > 180)
    out.longitude = 'Longitude harus angka -180…180.';
  return out;
};

/**
 * Shipments "Ubah": penerima & alamat before dispatch. Same input as the WMS
 * outbound form (typed address, Google suggestions, geocode, map pin). The
 * BE rewrites the items (address, coordinates, H3), moves the stop in any
 * open Dispatch preview and the WMS order that shipped it.
 *
 * A partner shipment "Set hub" applies to also gets a Hub field — the same
 * `POST /v1/shipments/hub:assign` as the bulk action, for this one shipment.
 * On a Retrieval the hub IS the drop-off, so picking one fills the address.
 */
const EditShipmentDestinationModal: React.FC<Props> = ({
  shipment,
  hubs = NO_HUBS,
  onClose,
  onSaved,
}) => {
  const initial = useMemo(
    () => (shipment ? draftOf(shipment) : null),
    [shipment]
  );
  const [draft, setDraft] = useState<DestinationDraft | null>(initial);
  const [errors, setErrors] = useState<
    Partial<Record<DestinationField, string>>
  >({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [distance, setDistance] = useState<DistanceDraft>(() =>
    distanceDraftOf(shipment?.distance)
  );
  // The hub the shipment is at on the server — moves once a save sets it, so
  // a retry after a failed address save doesn't re-send the hub.
  const [savedHubId, setSavedHubId] = useState('');
  const [hubId, setHubId] = useState('');

  const settableHub = !!shipment && canSetHub(shipment);
  const retrieval = shipment?.anchorRole === 'DESTINATION';
  const hubShipments = useMemo(
    () => (shipment ? [shipment] : []),
    [shipment]
  );
  const { options: hubOptions, pitstops, loading: loadingHubs } =
    useSetHubOptions(hubShipments, hubs, settableHub);

  useEffect(() => {
    setDraft(initial);
    setDistance(distanceDraftOf(shipment?.distance));
    setErrors({});
    setFormError(null);
    setSubmitting(false);
    const current = shipment?.hubId != null ? String(shipment.hubId) : '';
    setSavedHubId(current);
    setHubId(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const pickedHub = pitstops.find((p) => String(p.id) === hubId) ?? null;
  const hubChanged = settableHub && hubId !== '' && hubId !== savedHubId;
  // What the form holds when only the hub changed: on a Retrieval the
  // address follows the picked hub, so that alone is no address edit.
  const baseline = useMemo(
    () =>
      initial && hubChanged && retrieval && pickedHub
        ? { ...initial, ...addressOf(pickedHub) }
        : initial,
    [initial, hubChanged, retrieval, pickedHub]
  );

  const pickHub = (next: string) => {
    setHubId(next);
    if (!retrieval || !initial) return;
    const picked = pitstops.find((p) => String(p.id) === next);
    patch(
      picked && next !== savedHubId
        ? addressOf(picked)
        : {
            address: initial.address,
            latitude: initial.latitude,
            longitude: initial.longitude,
          }
    );
  };

  const patch = useCallback(
    (p: Partial<DestinationDraft>) =>
      setDraft((d) => (d ? { ...d, ...p } : d)),
    []
  );
  const fieldError = useCallback(
    (key: DestinationField) => errors[key] ?? null,
    [errors]
  );

  const close = () => {
    if (!submitting) onClose();
  };

  const moved =
    !!draft &&
    !!initial &&
    (draft.latitude !== initial.latitude ||
      draft.longitude !== initial.longitude);
  // Only a shipment that carries a distance gets one re-estimated.
  const autoDistance = moved && shipment?.distance != null;
  const shownDistance =
    moved && !distance.touched ? { value: '', touched: false } : distance;
  const distanceProblem = distanceError(distance);

  const submit = async () => {
    if (!shipment || !draft) return;
    const local = localErrors(draft);
    setErrors(local);
    setFormError(null);
    if (Object.keys(local).length || distanceProblem) return;
    setSubmitting(true);
    let hubSet = false;
    try {
      if (hubChanged) {
        const result = await shipmentsAPI.assignHub({
          shipmentIds: [shipment.id],
          pitstopId: Number(hubId),
        });
        const outcome = result.results[0]?.outcome;
        if (outcome !== 'assigned' && outcome !== 'reassigned') {
          setFormError(
            `Hub tidak di-set: ${
              outcome ? HUB_SKIP_REASON[outcome] : 'tidak ada hasil dari server'
            }.`
          );
          return;
        }
        hubSet = true;
        setSavedHubId(hubId);
        toast.success(`Hub di-set: ${result.pitstop.name}`, {
          description:
            outcome === 'assigned'
              ? 'Shipment sekarang AT_HUB — client menerima “Shipment siap ditangani”.'
              : 'Dipindah ke hub ini — status tetap, tercatat di timeline, tanpa webhook.',
        });
      }
      // Without a hub change "Simpan" always saves the address (unchanged
      // behaviour); with one, only when something besides the hub moved.
      const editAddress =
        !hubChanged ||
        !baseline ||
        !sameDraft(draft, baseline) ||
        distance.touched;
      if (!editAddress) {
        onSaved(await shipmentsAPI.get(shipment.id));
        return;
      }
      const updated = await shipmentsAPI.updateDestination(shipment.id, {
        receiverName: draft.recipientName.trim(),
        receiverPhone: draft.recipientPhone.trim(),
        address: draft.address.trim(),
        latitude: coord(draft.latitude) as number,
        longitude: coord(draft.longitude) as number,
        notes: draft.destNotes.trim(),
        distance: typedDistance(distance),
      });
      toast.success('Penerima & alamat diubah', {
        description: `${updated.waybill} · Dispatch & label ikut alamat baru — cetak ulang label bila sudah tercetak.`,
      });
      onSaved(updated);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Gagal menyimpan';
      setFormError(
        hubSet
          ? `Hub sudah di-set, tapi penerima & alamat gagal disimpan: ${message}`
          : message
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={shipment !== null}
      onClose={close}
      icon={<RiEditLine />}
      title={
        settableHub ? 'Ubah hub, penerima & alamat' : 'Ubah penerima & alamat'
      }
      subtitle={
        shipment ? `${shipment.waybill} · ${shipment.bookingId}` : undefined
      }
      size="lg"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={close}
            disabled={submitting}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={submitting}
            data-testid="shipment-edit-destination-submit"
          >
            Simpan
          </Button>
        </div>
      }
    >
      {shipment && draft && (
        <div
          className="flex flex-col gap-4"
          data-testid="shipment-edit-destination-dialog"
        >
          <p className="text-sm text-text-sub-600">
            Bisa diubah selama shipment belum di-dispatch. Titik (H3) dihitung
            ulang dan stop di preview dispatch ikut pindah.
          </p>
          {formError && (
            <Alert
              status="error"
              appearance="stroke"
              size="lg"
              title="Gagal menyimpan"
              data-testid="shipment-edit-destination-error"
            >
              {formError}
            </Alert>
          )}
          {settableHub && (
            <FormField
              label="Hub / pitstop"
              testId="shipment-edit-hub"
              hint={
                <span>
                  <span className="mr-1.5 inline-block align-middle">
                    <HandoverBadge anchorRole={shipment.anchorRole} />
                  </span>
                  {retrieval
                    ? 'Titik dropoff — alamat tujuan ikut hub yang dipilih.'
                    : 'Titik pickup shipment ini.'}
                  {hubChanged &&
                    (shipment.status === 'CREATED'
                      ? ' Setelah disimpan shipment jadi AT_HUB dan client menerima “Shipment siap ditangani”.'
                      : ' Pindah hub: status tetap, tercatat di timeline, tanpa webhook.')}
                </span>
              }
            >
              <SearchSelect
                placeholder="Cari atau pilih hub / pitstop…"
                options={hubOptions}
                value={hubId}
                onChange={(next) => pickHub(String(next))}
                loading={loadingHubs}
                disabled={submitting}
                emptyMessage="Tidak ada hub / pitstop"
              />
            </FormField>
          )}
          <OutboundDestinationStep
            client={null}
            transfer={false}
            addressOnly
            value={draft}
            onChange={patch}
            fieldError={fieldError}
          />
          <DistanceField
            value={shownDistance}
            auto={autoDistance}
            onChange={setDistance}
            error={distanceProblem}
            testId="shipment-edit-distance"
          />
        </div>
      )}
    </FormModal>
  );
};

export default EditShipmentDestinationModal;
